import { computed, type Ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type { BbelFunctionInfo, ScopeNode } from "@/types/bbel-types";

const BASE = `${import.meta.env.VITE_API_URL}/expressions`;

/**
 * The registered BBEL functions with editor metadata (name, category, signature, summary) for the
 * expression editor's autocomplete, categorised palette, and hover hints (#5683). The catalogue is
 * static per process, so it's cached indefinitely.
 */
export function useBbelFunctions() {
  const { data, isFetching, error } = useQuery({
    queryKey: ["bbel-functions"],
    queryFn: () => fetchWrapper.get<BbelFunctionInfo[]>(`${BASE}/functions`),
    staleTime: Infinity,
  });

  return {
    functions: computed(() => data.value ?? []),
    functionsLoading: isFetching,
    functionsError: error as Ref<ApiError | null>,
  };
}

/** Validation messages for one slot, index-aligned to the slots sent. Empty = valid. */
export interface SlotValidation {
  messages: string[];
}

export interface ValidateFieldResult {
  results: SlotValidation[];
}

/** The scope a validate call needs on the wire: segment + member-strictness + subtree. The editor's
 *  display fields (path, label) are dropped — the server reads only these. */
interface ValidationScopeNode {
  segment: string;
  closed?: boolean;
  children?: ValidationScopeNode[];
}

/** Project the editor scope tree to the lean wire shape, omitting display-only fields and empties. */
function toValidationScope(nodes: ScopeNode[]): ValidationScopeNode[] {
  return nodes.map((n) => ({
    segment: n.segment,
    ...(n.closed ? { closed: true } : {}),
    ...(n.children?.length ? { children: toValidationScope(n.children) } : {}),
  }));
}

/**
 * Live BBEL validation (#5683). Validates a field's `{{ … }}` slots against the client's scope tree
 * with the server's real expression validator (`POST /expressions/validate`) — authoritative, no
 * client/server drift. Driven imperatively from the CodeMirror linter, which owns the debounce; the
 * result is a pure function of `(scope, slots)`, so identical input is cached indefinitely and never
 * re-hits the server.
 */
export function useBbelValidation() {
  const queryClient = useQueryClient();
  return {
    validate: (roots: ScopeNode[], slots: string[]) => {
      const scope = toValidationScope(roots);
      return queryClient.fetchQuery({
        queryKey: ["bbel-validate", scope, slots],
        queryFn: () => fetchWrapper.post<ValidateFieldResult>(`${BASE}/validate`, { scope, slots }),
        staleTime: Infinity,
      });
    },
  };
}
